import Vue from 'vue';
import { mapGetters, mapMutations } from 'vuex';
import { debounce, get } from 'lodash';
import { planTypes, maxBizAndBizPlusLicenseCount } from '@/common/constants';
import { AddOrRemoveBasePlanActionTypes } from '../constants';
import { getPlanTotalLicense } from '@/common/service/validate';
import switchBasePlanDialog from '@/components/switch-base-plan-dialog.vue';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import { isAutoAddable } from '@/plans/ZOOM_MEETING/common/utils';
import { translateToEn, translation } from '@/plugins/i18n';
import EventTracking from '@/aspects/trackings';
import { getMeetingPlans } from '@/common/product';

// plan name i18n key cache
// to show license change message
const ChangedPlanCache = {
  timeId: null,
  planNameKeys: new Set(),
  msgInstance: null,
  currentPlanMaxLicense: new Map(),
};

/**
 * show banner message
 * @param {string} message message
 * @param {Object} trackingInfo tracking info
 * @param {string} trackingInfo.product product name
 * @param {string} trackingInfo.desc message desc
 */
const showMessage = (message, trackingInfo) => {
  const { msgInstance } = ChangedPlanCache;
  // do not stack notification
  if (msgInstance) {
    msgInstance.close();
  }
  EventTracking.buyInteraction('banner - product level', `${trackingInfo.product} - ${trackingInfo.desc} - impression`);
  ChangedPlanCache.msgInstance = Vue.prototype.$message({
    type: 'info',
    duration: 6000,
    message,
    showClose: true,
    onClose: () => {
      EventTracking.buyInteraction('banner - product level', `${trackingInfo.product} - ${trackingInfo.desc} - close`);
    },
  });
};

/**
 * sync addon license notification
 * @param {string} nameKey plan name i18n key
 * @param {string} basePlanNameKey base plan name i18n key
 */
export const showSyncAddonMessage = (nameKey, basePlanNameKey) => {
  const { timeId, planNameKeys } = ChangedPlanCache;
  planNameKeys.add(nameKey);
  // use debounce to collect notifications and merge into one
  if (timeId) {
    clearTimeout(timeId);
  }
  ChangedPlanCache.timeId = setTimeout(() => {
    const planNames = [...planNameKeys].map((key) => translation(key)).join(', ');
    const message = translation('billing.license_sync_addon', [planNames, translation(basePlanNameKey)]);
    const trackingProduct = [...planNameKeys].map((key) => translateToEn(key)).join(', ');
    const trackingInfo = {
      product: trackingProduct,
      desc: translateToEn('billing.license_sync_addon', [trackingProduct, translateToEn(basePlanNameKey)]),
    };
    showMessage(message, trackingInfo);
    planNameKeys.clear();
    ChangedPlanCache.timeId = null;
  }, 300);
};

/**
 * show common plan message
 * @param {object} options
 * @param {string} options.planNameKey plan name i18n key
 * @param {string} options.messageKey message i18n key
 * @param {string} options.messageParams message i18n params
 */
const showCommonPlanMessage = (options) => {
  const { planNameKey, messageKey, messageParams } = options;
  const message = translation(
    messageKey,
    messageParams.map((key) => translation(key)),
  );
  const trackingInfo = {
    product: translateToEn(planNameKey),
    desc: translateToEn(
      messageKey,
      messageParams.map((key) => translateToEn(key)),
    ),
  };
  showMessage(message, trackingInfo);
};

/**
 * sync base plan license notification
 * @param {string} planNameKey plan name i18n key
 * @param {string} basePlanNameKey base plan name i18n key
 */
export const showSyncBasePlanMessage = (planNameKey, basePlanNameKey) => {
  showCommonPlanMessage({
    planNameKey,
    messageKey: 'billing.license_sync_base',
    messageParams: [basePlanNameKey, planNameKey],
  });
};

/**
 * edit meeting plan notification
 * @param {string} planNameKey plan name i18n key
 * @param {string} basePlanNameKey meeting plan name i18n key
 */
const showEditMeetingPlanMessage = (planNameKey, basePlanNameKey) => {
  showCommonPlanMessage({
    planNameKey,
    messageKey: 'billing.addon_auto_add_tips',
    messageParams: [planNameKey, basePlanNameKey],
  });
};

/**
 * remove meeting plan notification
 * @param {string} planNameKey plan name i18n key
 * @param {string} basePlanNameKey meeting plan name i18n key
 */
const showRemoveMeetingPlanMessage = (planNameKey, basePlanNameKey) => {
  showCommonPlanMessage({
    planNameKey,
    messageKey: 'billing.addon_auto_remove_tips',
    messageParams: [planNameKey, basePlanNameKey],
  });
};

export default {
  data() {
    return {
      switchBasePlanDialog: new ServiceConfirm(switchBasePlanDialog),
      meetingPlanChangesTriggeredByAutoSync: false,
    };
  },
  computed: {
    ...mapGetters([
      'getBoughtMeetingPlans',
      'getSelectedPlans',
      'basicConfig'
    ]),
    mx_getBoughtMeetingPlansNoFreeTrial() {
      return this.getBoughtMeetingPlans.filter(item => !item.isOnlineFreeTrial)
    },
    mx_planType() {
      return this.planType;
    },
    mx_currentSelectedMeetingPlan() {
      const meetingPlans = getMeetingPlans(this.getSelectedPlans);
      const { category, billing_cycle, planNameKey, max } = get(meetingPlans, '0', {});
      return {
        category,
        planNameKey,
        maxHosts: max,
        billingCycle: billing_cycle,
        licenseQuantity: getPlanTotalLicense(meetingPlans),
      };
    },
    mx_currentMeetingPlanLicenseQuantity() {
      return this.mx_currentSelectedMeetingPlan.licenseQuantity;
    },
    mx_boughtMeetingPlan() {
      const { type, category, billing_cycle, planNameKey } = get(this.mx_getBoughtMeetingPlansNoFreeTrial, '0', {});
      return {
        category,
        planNameKey,
        planType: type,
        billingCycle: billing_cycle,
        licenseQuantity: getPlanTotalLicense(this.mx_getBoughtMeetingPlansNoFreeTrial),
      };
    },
    mx_currentSelectedAddonPlan() {
      const { planNameKey } = this.getSelectedPlans.find((p) => p.type === this.mx_planType) || {};
      return { planNameKey };
    },
    // add on plan max license quantity
    mx_maxAutoAddHosts() {
      // have bought meeting plan, no limit
      if (this.mx_boughtMeetingPlan.licenseQuantity) {
        return 999;
      }
      // standalone user, auto add license quantity can not exceed meeting plan
      return maxBizAndBizPlusLicenseCount;
    },
    mx_canAutoAdd() {
      return isAutoAddable();
    },
  },
  methods: {
    ...mapMutations([
      'updateSelectPlanByCategory',
      'setUpdateBasePlan',
      'addOrRemoveBasePlan',
      'setSelectedPlanLicense',
    ]),
    /**
     * auto update meeting plan license quantity
     * @param {number} basePlanNewSelectorNumber new meeting plan license quantity
     */
    mx_autoSyncBasePlanNumber(basePlanNewSelectorNumber) {
      const { category, billingCycle, planNameKey, licenseQuantity } = this.mx_currentSelectedMeetingPlan;
      if (basePlanNewSelectorNumber === licenseQuantity) {
        return;
      }
      this.meetingPlanChangesTriggeredByAutoSync = true;
      this.updateSelectPlanByCategory({
        category,
        billingCycle,
        cartInfo: { selected_number: basePlanNewSelectorNumber },
      });
      this.mx_onBasePlanUpdated(planNameKey);
    },

    /**
     * show notification
     * @param {string} basePlanNameKey base plan i18n key
     */
    mx_onBasePlanUpdated(basePlanNameKey) {
      const planNameKey = get(this.mx_currentSelectedAddonPlan, 'planNameKey');
      showSyncBasePlanMessage(planNameKey, basePlanNameKey);
    },

    /**
     * sync meeting plan license for standalone user
     * @param {number} newLicense new license quantity
     * @returns Promise
     */
    mx_syncMeetingPlanLicenseForStandaloneUser(newLicense) {
      const { licenseQuantity, maxHosts } = this.mx_currentSelectedMeetingPlan;
      return new Promise((resolve, reject) => {
        if (newLicense > maxHosts) {
          ChangedPlanCache.msgInstance && ChangedPlanCache.msgInstance.close();
          this.meetingPlanChangesTriggeredByAutoSync = true;
          this.switchBasePlanDialog
            .confirm({
              sourcePlan: this.mx_currentSelectedMeetingPlan,
              targetPlan: this.mx_currentSelectedAddonPlan,
              currentLicense: newLicense,
            })
            .then(() => {
              resolve();
            })
            .catch(() => {
              this.meetingPlanChangesTriggeredByAutoSync = false;
              reject(this.mx_currentMeetingPlanLicenseQuantity);
            });
        } else {
          if (newLicense > licenseQuantity) {
            this.mx_autoSyncBasePlanNumber(newLicense);
          }
          resolve();
        }
      });
    },

    /**
     * sync meeting plan license for has bought meeting user
     * @param {number} newLicense new license quantity
     * @returns Promise
     */
    mx_syncMeetingPlanLicenseForBoughtUser(newLicense) {
      const { planType, category, billingCycle, licenseQuantity, planNameKey } = this.mx_boughtMeetingPlan;
      if (newLicense > licenseQuantity || this.basicConfig.isExpireAccountWithPaidPlan) {
        // add meeting plan to cart
        if (!this.mx_currentMeetingPlanLicenseQuantity) {
          this.addOrRemoveBasePlan({ actionType: AddOrRemoveBasePlanActionTypes.ADD, planType: planTypes[planType] });
          this.meetingPlanChangesTriggeredByAutoSync = true;
          this.updateSelectPlanByCategory({
            category,
            billingCycle,
            cartInfo: { selected_number: newLicense },
          });
          showEditMeetingPlanMessage(this.mx_currentSelectedAddonPlan.planNameKey, planNameKey);
        } else {
          this.mx_autoSyncBasePlanNumber(newLicense);
        }
        this.$nextTick(() => {
          // Ensure the triggering of the watch in ZOOM_MEETING/add.
          this.setUpdateBasePlan({ update: true, from: this.title });
        });
      }
      // remove meeting plan from cart
      else if (this.mx_currentMeetingPlanLicenseQuantity) {
        this.meetingPlanChangesTriggeredByAutoSync = true;
        this.addOrRemoveBasePlan({ actionType: AddOrRemoveBasePlanActionTypes.REMOVE, planType: planTypes[planType] });
        showRemoveMeetingPlanMessage(this.mx_currentSelectedAddonPlan.planNameKey, planNameKey);
      }
      return Promise.resolve();
    },

    /**
     * remove bought base plan from cart
     */
    mx_removeBoughtBasePlan() {
      const { planType, licenseQuantity } = this.mx_boughtMeetingPlan;
      if (this.mx_currentMeetingPlanLicenseQuantity > 0 && licenseQuantity > 0) {
        this.meetingPlanChangesTriggeredByAutoSync = true;
        this.addOrRemoveBasePlan({ actionType: AddOrRemoveBasePlanActionTypes.REMOVE, planType: planTypes[planType] });
      }
    },

    /**
     * plan license change event
     * sync base plan license
     * @param {number} nv plan license number
     * @returns Promise
     */
    mx_handlePlanLicenseChange(nv) {
      ChangedPlanCache.currentPlanMaxLicense.set(this.mx_planType, nv);
      // have bought meeting plan
      if (this.mx_boughtMeetingPlan.licenseQuantity > 0) {
        const maxLicense = Math.max(...ChangedPlanCache.currentPlanMaxLicense.values());
        return this.mx_syncMeetingPlanLicenseForBoughtUser(maxLicense);
      }
      // standalone user
      return this.mx_syncMeetingPlanLicenseForStandaloneUser(nv);
    },
    /**
     * base plan license change
     * set Store to trigger base plan license change watch event
     * @param {number} nv base plan license number
     */
    mx_handleBasePlanLicenseChange(nv) {
      this.setSelectedPlanLicense({
        baseLicense: nv,
      });
    },
  },
  watch: {
    mx_currentMeetingPlanLicenseQuantity: {
      immediate: false,
      handler: debounce(function (nv) {
        if (!this.mx_canAutoAdd) return;
        // ignore changes triggered by auto sync
        if (this.meetingPlanChangesTriggeredByAutoSync) {
          this.meetingPlanChangesTriggeredByAutoSync = false;
          return;
        }
        this.mx_handleBasePlanLicenseChange(nv);
      }, 300),
    },
  },
  beforeDestroy() {
    ChangedPlanCache.currentPlanMaxLicense.delete(this.mx_planType);
  }
};
